/**
 * PlatformSection - Interactive tabs showcasing the 4 platform pillars
 * Chatbot IA | Expediente Médico | Analíticas | Panel de Control
 * Each tab shows description + visual mockup
 */
import React, { useState } from 'react';
import { Typography, Box, Container } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SettingsIcon from '@mui/icons-material/Settings';

import { COLORS, STYLES, TYPOGRAPHY, SPACING, TRANSITIONS } from './landingTheme';
import { ScrollReveal } from './animations';

// Platform pillars data
const PILLARS = [
  {
    id: 'chatbot',
    icon: SmartToyIcon,
    label: 'Chatbot IA',
    color: '#8b5cf6',
    title: 'Asistente médico inteligente en WhatsApp',
    description: 'Un chatbot que entiende síntomas, agenda citas, recuerda el historial de cada paciente y escala al doctor cuando es necesario. Funciona 24/7 sin intervención humana.',
    bullets: [
      'Diagnóstico diferencial con IA y niveles de urgencia',
      'Agenda, cancela y reagenda citas por conversación',
      'Memoria semántica: recuerda alergias, medicamentos e historial',
      'Análisis de imágenes médicas y documentos con visión IA',
    ],
  },
  {
    id: 'emr',
    icon: MedicalInformationIcon,
    label: 'Expediente',
    color: '#10b981',
    title: 'Expediente médico electrónico integrado',
    description: 'Registro clínico digital por paciente con historial completo, notas de consulta y seguimiento. Todo accesible desde un solo lugar, sincronizado con las conversaciones del chatbot.',
    bullets: [
      'Ficha clínica completa por paciente',
      'Historial de consultas, diagnósticos y tratamientos',
      'Notas médicas y seguimiento longitudinal',
      'Datos sincronizados automáticamente desde el chatbot',
    ],
  },
  {
    id: 'analytics',
    icon: BarChartIcon,
    label: 'Analíticas',
    color: '#0ea5e9',
    title: 'Métricas en tiempo real de tu consultorio',
    description: 'Visualiza el rendimiento de tu chatbot, el volumen de conversaciones, uso de tokens de IA y tendencias de consultas. Toma decisiones basadas en datos reales.',
    bullets: [
      'Dashboard de conversaciones: completadas, escaladas, activas',
      'Consumo de servicios (IA, mensajería, embeddings) por chatbot y por mes',
      'Historial exportable de todas las conversaciones',
      'Tendencias de consulta y horarios pico',
    ],
  },
  {
    id: 'dashboard',
    icon: DashboardCustomizeIcon,
    label: 'Panel',
    color: '#f59e0b',
    title: 'Panel de control para gestionar todo',
    description: 'Administra múltiples chatbots, configura personalidad y base de conocimiento, gestiona planes y suscripciones. Todo desde una interfaz intuitiva.',
    bullets: [
      'Multi-chatbot: cada doctor con su propio asistente',
      'Constructor de personalidad y base de conocimiento',
      'Gestión de planes y suscripción por chatbot',
      'Centro de ayuda y configuración avanzada',
    ],
  },
];

// Mockup: WhatsApp chat for Chatbot tab
const ChatbotMockup = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
    {/* WhatsApp header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #25D366, #128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <WhatsAppIcon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>Dra. García - NARA</Typography>
        <Typography sx={{ fontSize: 10, color: '#25D366' }}>en línea</Typography>
      </Box>
    </Box>
    {/* Chat bubbles */}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Box sx={{ background: '#dcf8c6', px: 1.5, py: 1, borderRadius: '12px 12px 4px 12px', maxWidth: '80%' }}>
        <Typography sx={{ fontSize: 11.5, color: '#1a1a1a' }}>Me duele mucho la cabeza desde ayer y tengo fiebre</Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
      <Box sx={{ background: '#fff', px: 1.5, py: 1, borderRadius: '12px 12px 12px 4px', maxWidth: '85%', border: '1px solid #f0f0f0' }}>
        <Typography sx={{ fontSize: 11.5, color: '#1a1a1a' }}>Entiendo tu preocupación. Voy a analizar tus síntomas con detalle para orientarte mejor. Las posibles causas incluyen:</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#1a1a1a', mt: 0.5 }}>
          {'📋 1. Gripe o resfriado común\n🩺 2. Sinusitis\n🔔 3. Migraña con fiebre'}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Box sx={{ background: '#dcf8c6', px: 1.5, py: 1, borderRadius: '12px 12px 4px 12px' }}>
        <Typography sx={{ fontSize: 11.5, color: '#1a1a1a' }}>Quiero agendar una cita para mañana</Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
      <Box sx={{ background: '#fff', px: 1.5, py: 1, borderRadius: '12px 12px 12px 4px', border: '1px solid #f0f0f0' }}>
        <Typography sx={{ fontSize: 11.5, color: '#1a1a1a' }}>Estos son los horarios disponibles para mañana:</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#1a1a1a', mt: 0.5 }}>{'✅ 9:00 AM\n✅ 10:30 AM\n✅ 2:00 PM'}</Typography>
      </Box>
    </Box>
  </Box>
);

// Mockup: EMR patient card
const EMRMockup = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
    {/* Patient header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PersonIcon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>María González López</Typography>
        <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>Paciente #1042 &bull; 34 años</Typography>
      </Box>
    </Box>
    {/* Info rows */}
    {[
      { label: 'Última consulta', value: '15 enero 2026' },
      { label: 'Alergias', value: 'Penicilina' },
      { label: 'Medicamentos', value: 'Omeprazol 20mg' },
    ].map((item, idx) => (
      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>{item.label}</Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: COLORS.textPrimary }}>{item.value}</Typography>
      </Box>
    ))}
    {/* Notes section */}
    <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 2, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#15803d', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Última nota</Typography>
      <Typography sx={{ fontSize: 11, color: '#166534', lineHeight: 1.5 }}>Paciente refiere cefalea recurrente. Se solicitan estudios de gabinete. Seguimiento en 2 semanas.</Typography>
    </Box>
    {/* History count */}
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, background: '#f1f5f9', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: COLORS.textPrimary }}>12</Typography>
        <Typography sx={{ fontSize: 9, color: COLORS.textMuted }}>Consultas</Typography>
      </Box>
      <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, background: '#f1f5f9', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: COLORS.textPrimary }}>3</Typography>
        <Typography sx={{ fontSize: 9, color: COLORS.textMuted }}>Diagnósticos</Typography>
      </Box>
      <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, background: '#f1f5f9', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: COLORS.textPrimary }}>8</Typography>
        <Typography sx={{ fontSize: 9, color: COLORS.textMuted }}>Memorias IA</Typography>
      </Box>
    </Box>
  </Box>
);

// Mockup: Analytics dashboard
const AnalyticsMockup = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
    {/* KPI row */}
    <Box sx={{ display: 'flex', gap: 1 }}>
      {[
        { value: '247', label: 'Conversaciones', color: '#0ea5e9', icon: ChatBubbleOutlineIcon },
        { value: '89%', label: 'Completadas', color: '#10b981', icon: TrendingUpIcon },
        { value: '1.2s', label: 'Respuesta', color: '#8b5cf6', icon: TrendingUpIcon },
      ].map((kpi, idx) => {
        const KpiIcon = kpi.icon;
        return (
          <Box key={idx} sx={{ flex: 1, p: 1, borderRadius: 2, background: `${kpi.color}08`, border: `1px solid ${kpi.color}18` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <KpiIcon sx={{ fontSize: 12, color: kpi.color }} />
              <Typography sx={{ fontSize: 9, color: COLORS.textMuted }}>{kpi.label}</Typography>
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: kpi.color }}>{kpi.value}</Typography>
          </Box>
        );
      })}
    </Box>
    {/* Mini bar chart */}
    <Box sx={{ p: 1.5, borderRadius: 2, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Conversaciones - última semana</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 60 }}>
        {[35, 42, 28, 55, 48, 62, 45].map((val, idx) => (
          <Box key={idx} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
            <Box sx={{ width: '100%', height: val, borderRadius: 1, background: idx === 5 ? 'linear-gradient(135deg, #0ea5e9, #0284c7)' : '#e2e8f0', transition: TRANSITIONS.normal }} />
            <Typography sx={{ fontSize: 8, color: COLORS.textMuted }}>
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'][idx]}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
    {/* Token usage */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0' }}>
      <Box>
        <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>Tokens IA usados</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: COLORS.textPrimary }}>324,500 <Typography component="span" sx={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 400 }}>/ 500,000</Typography></Typography>
      </Box>
      <Box sx={{ width: 60, height: 6, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden' }}>
        <Box sx={{ width: '65%', height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #0ea5e9, #06b6d4)' }} />
      </Box>
    </Box>
  </Box>
);

// Mockup: Dashboard panel
const DashboardMockup = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SettingsIcon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>Clínica San Rafael</Typography>
        <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>3 chatbots activos &bull; Plan Pro</Typography>
      </Box>
    </Box>
    {/* Chatbot cards mini */}
    {[
      { name: 'Dr. García', status: 'Activo', msgs: '89', color: '#8b5cf6' },
      { name: 'Dra. López', status: 'Activo', msgs: '124', color: '#0ea5e9' },
      { name: 'Dr. Pérez', status: 'Configurando', msgs: '0', color: '#f59e0b' },
    ].map((bot, idx) => (
      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: bot.status === 'Activo' ? '#10b981' : '#f59e0b', flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.textPrimary }}>{bot.name}</Typography>
          <Typography sx={{ fontSize: 9, color: COLORS.textMuted }}>{bot.status}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: bot.color }}>{bot.msgs}</Typography>
          <Typography sx={{ fontSize: 8, color: COLORS.textMuted }}>msgs/mes</Typography>
        </Box>
      </Box>
    ))}
    {/* Quick actions */}
    <Box sx={{ display: 'flex', gap: 1 }}>
      {[
        { label: 'Nuevo chatbot', color: '#8b5cf6' },
        { label: 'Base conocimiento', color: '#0ea5e9' },
      ].map((action, idx) => (
        <Box key={idx} sx={{ flex: 1, py: 1, borderRadius: 1.5, background: `${action.color}08`, border: `1px solid ${action.color}18`, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: action.color }}>+ {action.label}</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

// Mockup mapping
const MOCKUP_MAP = {
  chatbot: ChatbotMockup,
  emr: EMRMockup,
  analytics: AnalyticsMockup,
  dashboard: DashboardMockup,
};

export default function PlatformSection() {
  const [activeTab, setActiveTab] = useState(0);
  const activePillar = PILLARS[activeTab];
  const MockupComponent = MOCKUP_MAP[activePillar.id];

  return (
    <Box sx={{ py: SPACING.sectionY, background: COLORS.gray50, position: 'relative', overflow: 'hidden' }}>
      {/* Decorative blob */}
      <Box sx={{ position: 'absolute', top: '-10%', left: '-5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245, 158, 11, 0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        {/* Section header */}
        <ScrollReveal>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              color: COLORS.accent,
              fontSize: TYPOGRAPHY.tiny,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              mb: 1.5,
            }}
          >
            Plataforma
          </Typography>
          <Typography
            variant="h2"
            sx={{ ...STYLES.sectionTitle }}
          >
            Más que un chatbot, una plataforma completa
          </Typography>
          <Typography
            sx={{ ...STYLES.sectionSubtitle }}
          >
            Cuatro pilares que trabajan juntos para transformar la gestión de tu consultorio
          </Typography>
        </Box>
        </ScrollReveal>

        {/* Tabs */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 0.5, sm: 1 },
            mb: { xs: 4, md: 5 },
            flexWrap: 'wrap',
          }}
        >
          {PILLARS.map((pillar, idx) => {
            const TabIcon = pillar.icon;
            const isActive = activeTab === idx;
            return (
              <Box
                key={pillar.id}
                onClick={() => setActiveTab(idx)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: { xs: 1.5, sm: 2.5 },
                  py: { xs: 1, sm: 1.2 },
                  borderRadius: SPACING.sectionGap.xs,
                  cursor: 'pointer',
                  transition: TRANSITIONS.normal,
                  border: '1.5px solid',
                  borderColor: isActive ? pillar.color : COLORS.gray200,
                  background: isActive ? `${pillar.color}0a` : COLORS.white,
                  boxShadow: isActive ? `0 4px 16px ${pillar.color}18` : '0 1px 3px rgba(0,0,0,0.04)',
                  '&:hover': {
                    borderColor: pillar.color,
                    background: `${pillar.color}06`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <TabIcon
                  sx={{
                    fontSize: { xs: 18, sm: 20 },
                    color: isActive ? pillar.color : COLORS.textMuted,
                    transition: TRANSITIONS.fast,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '0.78rem', sm: '0.88rem' },
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? pillar.color : COLORS.textSecondary,
                    transition: TRANSITIONS.fast,
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {pillar.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Content area — description + mockup */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 3, md: 5 },
            alignItems: 'center',
          }}
        >
          {/* Left: text content */}
          <Box sx={{ order: { xs: 2, md: 1 } }}>
            <Typography
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                fontWeight: 800,
                color: COLORS.textPrimary,
                mb: 2,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
              }}
            >
              {activePillar.title}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.9rem', md: '1rem' },
                color: COLORS.textSecondary,
                lineHeight: 1.7,
                mb: 3,
              }}
            >
              {activePillar.description}
            </Typography>
            {/* Bullet points */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {activePillar.bullets.map((bullet, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 20,
                      color: activePillar.color,
                      mt: 0.1,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '0.85rem', md: '0.9rem' },
                      color: COLORS.textSecondary,
                      lineHeight: 1.5,
                    }}
                  >
                    {bullet}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right: visual mockup */}
          <Box sx={{ order: { xs: 1, md: 2 } }}>
            <Box
              sx={{
                borderRadius: 4,
                background: COLORS.white,
                border: `1px solid ${COLORS.gray200}`,
                boxShadow: `0 8px 32px ${activePillar.color}08, 0 2px 8px rgba(0,0,0,0.04)`,
                overflow: 'hidden',
                position: 'relative',
                transition: TRANSITIONS.normal,
                // Top color bar
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, ${activePillar.color}, ${activePillar.color}60)`,
                },
              }}
            >
              <MockupComponent />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
